import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import LoadingSpinner from './components/common/LoadingSpinner';

// Layout
const Layout = lazy(() => import('./components/layout/Layout'));

// Auth
const Login = lazy(() => import('./pages/auth/Login'));
const AcceptInvite = lazy(() => import('./pages/auth/AcceptInvite'));

// Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Customers
const CustomerList = lazy(() => import('./pages/customers/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/customers/CustomerDetail'));

// Subscriptions
const SubscriptionList = lazy(() => import('./pages/subscriptions/SubscriptionList'));
const SubscriptionDetail = lazy(() => import('./pages/subscriptions/SubscriptionDetail'));
const SubscriptionForm = lazy(() => import('./pages/subscriptions/SubscriptionForm'));

// Invoices
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const InvoiceDetail = lazy(() => import('./pages/invoices/InvoiceDetail'));

// Payments
const PaymentList = lazy(() => import('./pages/payments/PaymentList'));

// Product Catalog
const ProductList = lazy(() => import('./pages/products/ProductList'));

// Reports
const Reports = lazy(() => import('./pages/reports/Reports'));

// Settings
const Settings = lazy(() => import('./pages/settings/Settings'));

// Integrations
const Integrations = lazy(() => import('./pages/integrations/Integrations'));

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Page loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Customers */}
          <Route path="customers" element={<CustomerList />} />
          <Route path="customers/:id" element={<CustomerDetail />} />

          {/* Subscriptions */}
          <Route path="subscriptions" element={<SubscriptionList />} />
          <Route path="subscriptions/new" element={<SubscriptionForm />} />
          <Route path="subscriptions/:id" element={<SubscriptionDetail />} />
          <Route path="subscriptions/:id/edit" element={<SubscriptionForm />} />

          {/* Invoices */}
          <Route path="invoices" element={<InvoiceList />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />

          {/* Payments */}
          <Route path="payments" element={<PaymentList />} />

          {/* Product Catalog */}
          <Route path="products" element={<ProductList />} />
          <Route path="plans" element={<ProductList />} />
          <Route path="addons" element={<ProductList />} />
          <Route path="coupons" element={<ProductList />} />

          {/* Credit Notes */}
          {/* <Route path="creditnotes" element={<CreditNoteList />} /> */}

          {/* Reports */}
          <Route path="reports" element={<Reports />} />
          <Route path="reports/:section" element={<Reports />} />

          {/* Settings */}
          <Route path="settings" element={<Settings />} />
          <Route path="settings/:section" element={<Settings />} />

          {/* Integrations */}
          <Route path="integrations" element={<Integrations />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
